import { AutoAwesome, ExpandMore, ShareOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkRemoveOutlinedIcon from "@mui/icons-material/BookmarkRemoveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoveUpOutlinedIcon from "@mui/icons-material/MoveUpOutlined";
import PeopleIcon from "@mui/icons-material/People";
import { Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { assetService, cacheService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { Licences } from "../../../Core/Api/Types/licences";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import {
  GetChildsRecursivelyByIds,
  IsObjectShareable,
  IsPermissionGranted,
} from "../../../Core/Utils/asset-object-utils";
import { RemoveFromResultObjectsById } from "../../../Redux/Reducers/redux-slice-asset";
import { GetCurrentObj, RemoveObjs, TrashObj } from "../../../Redux/Reducers/redux-slice-drive";
import {
  GetTreeObj,
  MoveChildrenInTreeObj,
  RemoveChildrenFromTreeObj,
  TreeContext,
} from "../../../Redux/Reducers/redux-slice-tree";
import { useAppSelector } from "../../../Redux/redux-store";
import { UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import useAssetTreeManipulation from "../../../Utils/Hooks/useAssetTreeManipulation";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import useTrashActions from "../../../Utils/Hooks/useTrash";
import CreateAccessButton from "../../Access/Shared/create-access-button";
import { MultiDownloadMenuItem } from "../../Drive/Common/download-menu-item";
import { ModalContext } from "../../Modals";
import ModalApplyJobOrWorkflow, {
  ModalApplyJobOrWorkflowElementProps,
} from "../../Modals/ModalApplyJobOrWorkflow/modal-apply-job-or-workflow";
import ModalQualityCheck, { ModalQualityCheckElementProps } from "../../Modals/ModalAssetActions/modal-quality-check";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import ModalEditMetadata, { ModalEditMetadataElementProps } from "../../Modals/ModalEditMetadata/modal-edit-metadata";
import ModalMoveAssetDrive, {
  ModalMoveAssetDriveElementProps,
} from "../../Modals/ModalMoveAsset/modal-move-asset-drive";
import ModalRequestPurge, { ModalRequestPurgeElementProps } from "../../Modals/ModalPurgeAsset/modal-request-purge";
import ModalSetFollowers, { ModalSetFollowersElementProps } from "../../Modals/ModalSetFollowers/modal-set-followers";
import SelectModeBanner from "../SelectModeBanner";

export interface DriveSelectModeBannerProps {
  handleCloseClick: React.MouseEventHandler;
  items: DbAssetObjectInfos[];
  setItems?: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
  editMode?: boolean;
  layer: ObjectLayer;
  itemsCanBeShared: boolean;
  setReload?: React.Dispatch<React.SetStateAction<number>>;
}

const DriveSelectModeBanner: React.FC<DriveSelectModeBannerProps> = (props) => {
  const { t } = useTranslation();
  const { items, handleCloseClick, editMode, layer, itemsCanBeShared, setReload, setItems } = props;
  const { AddItemsByIdsToCollection } = useCollectionActions();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdsToBasket } = useBasketActions();
  const enqueueSnackbar = useCustomSnackbar();
  const trashActions = useTrashActions();
  const dispatch = useDispatch();
  const currentObject = useAppSelector(GetCurrentObj);
  const platformMode = cacheService.GetPlatformMode();
  const { getIntersectLayerOfSelection, canMoveSelection, moveSelectionToAsset } = useAssetTreeManipulation();
  const treeObject = useAppSelector(GetTreeObj(TreeContext.DriveTreeview));
  const enqueueCustomSnackbar = useCustomSnackbar();

  const allShareable = React.useMemo(() => {
    return !items.some((x) => !IsObjectShareable(x));
  }, [items]);

  const anyDrive = items.some((x) => x.classification === ObjectClass.Drive);

  const [selectedAssets, setSelectedAssets] = React.useState<DbAssetObjectInfos[]>([]);
  const [buttonsPerms, setButtonsPerms] = React.useState<{
    showQcButton: boolean;
    userCanOrderQc: boolean;
    userHasAclPurgeAccepted: boolean;
    userHasPurgeCreate: boolean;
    userHasElevationWishlistCreate: boolean;
    showPurgeButton: boolean;
    userCanOrderPurge: boolean;
    userCanStartJob: boolean;
    userCanSwitchToMezzAuto: boolean;
    userCanCreateShare: boolean;
    userCanDeleteAsset: boolean;
    userCanAssignAsset: boolean;
    userCanDownloadAsset: boolean;
  }>({
    showQcButton: false,
    userCanOrderQc: false,
    userHasAclPurgeAccepted: false,
    userHasPurgeCreate: false,
    userHasElevationWishlistCreate: false,
    showPurgeButton: false,
    userCanOrderPurge: false,
    userCanStartJob: false,
    userCanSwitchToMezzAuto: false,
    userCanCreateShare: false,
    userCanDeleteAsset: false,
    userCanAssignAsset: false,
    userCanDownloadAsset: false,
  });

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMoreEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const handleAddToCollectionClick = () => {
    AddItemsByIdsToCollection(items.map((x) => x.id));
  };

  const handleAddToBasketClick = () => {
    addObjectByIdsToBasket(items.map((x) => x.id));
  };

  const handleQCOrder = () => {
    if (!openModal) return;

    openModal<ModalQualityCheckElementProps>(ModalQualityCheck, {
      assets: selectedAssets,
    });
  };

  const handlePurgeAssetsClick = () => {
    if (!openModal) {
      return;
    }

    openModal<ModalRequestPurgeElementProps>(ModalRequestPurge, {
      title: t("modal:title.confirmPurgeRequestsCreation"),
      assetIds: items.map((x) => x.id),
    });
  };

  const handleStartJobClick = () => {
    if (!openModal) return;
    openModal<ModalApplyJobOrWorkflowElementProps>(ModalApplyJobOrWorkflow, {
      isJob: true,
      assetIds: items.map((x) => x.id),
    });
  };

  const handleMezzAutoCreationClick = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.autoMezzCreationReason"),
      title: t("modal:title.switchAssetToMezzAutoClass"),
      onConfirm: (comment: string | undefined) => {
        items
          .map((x) => x.id)
          .forEach((assetId) => {
            assetService
              .SwitchObjectClass({ assetId: assetId, classification: ObjectClass.MezzAuto, annotation: comment ?? "" })
              .then((response) => {
                enqueueSnackbar(t("alert:success.mezzAutoGeneratedForAssets", { id: assetId }), "success");
              });
          });
      },
    });
  };

  const handleAssignAssetClick = () => {
    if (!openModal) return;

    openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
      objectIds: items.map((x) => x.id),
      objectType: "asset",
      currentFollowersIds: [],
      onConfirm: () => {
        setReload && setReload((x) => x + 1);
        setItems && setItems([]);
      },
    });
  };

  const handleDeletedObject = (objectId: number) => {
    dispatch(RemoveChildrenFromTreeObj({ children: [objectId], context: TreeContext.DriveTreeview }));
    dispatch(TrashObj({ objId: objectId }));
    setItems && setItems((x) => x && x.filter((y) => y.id !== objectId));
  };

  const handleDeleteClick = () => {
    if (items.every((x) => x.trash)) {
      trashActions.DeleteForeverObjectByIds(items.map((x) => x.id));
    } else {
      items &&
        trashActions.TrashObjectByIds(
          items.map((x) => x.id),
          handleDeletedObject
        );
    }
  };

  const handleMoveAssets = React.useCallback(
    (selectedParentId: number, closeModal: () => void, disableLoader: () => void) => {
      if (items.map((x) => x.parentObjectId).some((x) => x !== selectedParentId))
        moveSelectionToAsset(
          items.flatMap((o) => o.id),
          selectedParentId,
          () => {
            dispatch(
              MoveChildrenInTreeObj({
                parentObjectId: selectedParentId,
                children: items.map((x) => x.id),
                context: TreeContext.DriveTreeview,
              })
            );
            dispatch(RemoveObjs(items.map((x) => x.id)));
            closeModal();
          },
          disableLoader
        );
      else closeModal();
    },
    [items]
  );

  const onClickMoveSelection = React.useCallback(() => {
    if (!openModal) return;

    openModal<ModalMoveAssetDriveElementProps>(ModalMoveAssetDrive, {
      headObjectId: treeObject!.id,
      selectableObjectLayers: getIntersectLayerOfSelection(items),
      objectIdsToDisable: items.flatMap((o) => o.id).concat(items.flatMap((o) => o.parentObjectId!)),
      onMoveAssetClick: handleMoveAssets,
      objectIdsToHilight: items.flatMap((o) => o.parentObjectId!),
    });
  }, [openModal, items]);

  const handleShareClick = () => {
    if (!openModal) return;
    openModal(ModalCreateShare, { assetIds: items.map((item) => item.id) });
  };

  const openModalMetadata = () => {
    if (!openModal) return;
    if (items.length === 1) openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: items });
    else openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: items });
  };

  React.useEffect(() => {
    const userHasAclPurgeAccepted =
      UserHasAcl(AclTypes.aclWorkflowPurgeCreateWithAcceptedAssetStatus) && platformMode !== PlatformMode.Browser;
    const userHasPurgeCreate = UserHasAcl(AclTypes.aclWorkflowPurgeCreate) && platformMode !== PlatformMode.Browser;
    const userHasElevationWishlistCreate =
      UserHasAcl(AclTypes.aclElevationWishlistCreate) && platformMode !== PlatformMode.Browser;

    setButtonsPerms({
      // show button only if user has acl
      showQcButton: UserHasAcl(AclTypes.aclOrderQualityCheckCreate) && platformMode !== PlatformMode.Browser,
      // set to false when no selection
      userCanOrderQc: false,
      // store acls for selection rights
      userHasAclPurgeAccepted,
      userHasPurgeCreate,
      userHasElevationWishlistCreate,
      // show button only if user has one of those acls
      showPurgeButton: userHasAclPurgeAccepted || userHasPurgeCreate || userHasElevationWishlistCreate,
      // set to false when no selection
      userCanOrderPurge: false,
      userCanStartJob: UserHasAcl(AclTypes.aclJobRuleApply),
      userCanSwitchToMezzAuto:
        UserHasAcl(AclTypes.aclSuperUser) &&
        UserHasLicence(Licences.NormalizerTool) &&
        platformMode !== PlatformMode.Browser,
      userCanCreateShare: UserHasAcl(AclTypes.aclShareCreate) && UserHasLicence(Licences.ShareScreening),
      userCanDeleteAsset: UserHasAcl(AclTypes.aclAssetDelete),
      userCanAssignAsset: cacheService.GetPlatformMode() === PlatformMode.Adam,
      userCanDownloadAsset: false,
    });
  }, []);

  React.useEffect(() => {
    if (currentObject) {
      // get selected assets from tree
      const selectedAssets = items.filter((x) => x.type !== ObjectType.Folder);
      // get selected folders and all children from tree
      const selectedFolders = GetChildsRecursivelyByIds(
        treeObject!,
        items.filter((x) => x.type === ObjectType.Folder).map((x) => x.id)
      );

      setSelectedAssets([...selectedAssets, ...selectedFolders]);
      setButtonsPerms((prevState) => {
        return {
          // copy previous state
          ...prevState,
          // overrides
          // user can order qc only on Audio and Video types
          userCanOrderQc: !selectedAssets.some(
            (x) => x.type !== ObjectType.Audio && x.type !== ObjectType.Video && x.type !== ObjectType.ImageSequence
          ),
          // user can purge if he has acls, but on accepted assets it needs one particular acl
          userCanOrderPurge:
            (selectedAssets.some((x) => x.status === ObjectStatus.Accepted)
              ? prevState.userHasAclPurgeAccepted
              : prevState.userHasPurgeCreate) || prevState.userHasElevationWishlistCreate,
          userCanSwitchToMezzAuto:
            selectedAssets.every((x) => x.classification === ObjectClass.Raw) &&
            UserHasAcl(AclTypes.aclSuperUser) &&
            UserHasLicence(Licences.NormalizerTool) &&
            platformMode !== PlatformMode.Browser,
          userCanCreateShare:
            UserHasAcl(AclTypes.aclShareCreate) &&
            !selectedAssets.some((x) => !IsPermissionGranted(x, PermissionType.Share)),
          userCanDeleteAsset:
            UserHasAcl(AclTypes.aclAssetDelete) &&
            !selectedAssets.some((x) => !IsPermissionGranted(x, PermissionType.Trash)),
          userCanDownloadAsset: !selectedAssets.some((x) => !IsPermissionGranted(x, PermissionType.Download)),
        };
      });
    }
  }, [items]);

  return (
    <SelectModeBanner text={t("banner:body.itemSelected", { count: items.length })} onCloseClick={handleCloseClick}>
      {editMode ? (
        <>
          <Button
            sx={{ mr: 1 }}
            startIcon={<DriveFileMoveOutlinedIcon />}
            onClick={() => handleAddToCollectionClick()}
            variant="whiteSoft"
          >
            {t("button:moveTo")}...
          </Button>
          <Button sx={{ mr: 1 }} startIcon={<BookmarkRemoveOutlinedIcon />} variant="whiteSoft">
            {t("button:removeFromList")}
          </Button>
        </>
      ) : (
        <>
          <Button
            aria-controls={moreOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={moreOpen ? "true" : undefined}
            onClick={handleMoreClick}
            variant="whiteSoft"
            size="small"
            endIcon={<ExpandMore />}
          >
            {t("common:more")}
          </Button>

          <Divider
            orientation="vertical"
            flexItem
            sx={(theme) => ({ borderColor: theme.palette.backgroundBoldAlpha.border, m: 0.5 })}
          />

          {buttonsPerms.userCanCreateShare &&
            (layer === ObjectLayer.Asset || layer === ObjectLayer.Group) &&
            allShareable && (
              <Stack direction="row" spacing={1}>
                {/* {!areAllAssetsFromDrive ? ( */}
                <CreateAccessButton assets={selectedAssets} variant="whiteSoft" />
                <IconButton
                  onClick={handleShareClick}
                  disabled={items.length === 0 || !itemsCanBeShared}
                  size="small"
                  color="whiteSoft"
                >
                  <ShareOutlined />
                </IconButton>
              </Stack>
            )}

          <IconButton size="small" color="whiteSoft" onClick={() => handleAddToCollectionClick()}>
            <BookmarkBorderOutlinedIcon />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorMoreEl}
            open={moreOpen}
            onClose={handleMoreClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem onClick={openModalMetadata}>
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              <ListItemText>{t("common:editMetadata")}</ListItemText>
            </MenuItem>
            <MultiDownloadMenuItem
              objs={items}
              handleMoreClose={handleMoreClose}
              canDownload={buttonsPerms.userCanDownloadAsset}
            />
            {canMoveSelection(items) && (
              <MenuItem onClick={onClickMoveSelection}>
                <ListItemIcon>
                  <MoveUpOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{t("menu:item.moveTo")}...</ListItemText>
              </MenuItem>
            )}
            {buttonsPerms.userCanAssignAsset && (layer === ObjectLayer.Asset || layer === ObjectLayer.Group) && (
              <MenuItem onClick={handleAssignAssetClick}>
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText>{t("common:setFollowers")}</ListItemText>
              </MenuItem>
            )}
            {buttonsPerms.showQcButton && layer === ObjectLayer.Asset && !anyDrive && (
              <MenuItem onClick={handleQCOrder} disabled={items.length === 0 || !buttonsPerms.userCanOrderQc}>
                <ListItemIcon>
                  <CheckIcon />
                </ListItemIcon>
                <ListItemText>{t("menu:item.orderQualityCheck")}</ListItemText>
              </MenuItem>
            )}
            {platformMode === PlatformMode.Distribution && buttonsPerms.showPurgeButton && !anyDrive && (
              <MenuItem
                onClick={handlePurgeAssetsClick}
                disabled={items.length === 0 || !buttonsPerms.userCanOrderPurge}
              >
                <ListItemIcon>
                  <HighlightOffIcon />
                </ListItemIcon>
                <ListItemText>{t("common:requestPurge")}</ListItemText>
              </MenuItem>
            )}
            {buttonsPerms.userCanStartJob && (layer === ObjectLayer.Asset || layer === ObjectLayer.Group) && (
              <MenuItem onClick={handleStartJobClick} disabled={items.length === 0}>
                <ListItemIcon>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText>{t("menu:item.startJob")}</ListItemText>
              </MenuItem>
            )}
            {buttonsPerms.userCanSwitchToMezzAuto && layer === ObjectLayer.Asset && (
              <MenuItem onClick={handleMezzAutoCreationClick} disabled={items.length === 0}>
                <ListItemIcon>
                  <AutoAwesome />
                </ListItemIcon>
                <ListItemText>{t("menu:item.autoCreateMezzAssets")}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          <Divider
            orientation="vertical"
            flexItem
            sx={(theme) => ({ borderColor: theme.palette.backgroundBoldAlpha.border, m: 0.5 })}
          />

          {buttonsPerms.userCanDeleteAsset && (layer === ObjectLayer.Asset || layer === ObjectLayer.Group) && (
            <Button startIcon={<DeleteIcon />} onClick={handleDeleteClick} color="error" variant="contained">
              {anyDrive ? t("common:trash") : t("common:delete")}
            </Button>
          )}
        </>
      )}
      <Button variant="white" startIcon={<AddIcon />} onClick={handleAddToBasketClick}>
        {t("common:addToBasket")}
      </Button>
    </SelectModeBanner>
  );
};

export default DriveSelectModeBanner;
